import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  ChevronRight as CaretRightIcon,
  Layers3 as LayersIcon,
  Search as SearchIcon,
  Shield as ShieldIcon
} from 'lucide-react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { decodeRouteParam } from '@atbash-labs/plantview-links'
import {
  findSrsFunctionGroupBySectionId,
  formatSilLevel,
  groupProtectiveFunctionsBySrsGroup,
  matchesProtectiveFunctionSearch,
  protectiveFunctionTypeLabel,
  useGetPlantProtectiveFunctionsQuery,
  type PlantProtectiveFunctionSummary
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Empty } from '../../components/primitives/Empty'
import { Input } from '../../components/ui/input'
import { plantProtectiveFunctionHref, plantSafetyIndexHref } from '../../lib/plant-links'
import { SectionHeader } from './entity/SectionHeader'
import { SilRequirementBadge } from './SilRequirementBadge'

/**
 * `/plant/safety/groups/:sectionId` — one site-scoped SRS function group.
 *
 * The route is anchored by any member `SrsSection` id, then folds every
 * section with the same `function_group` at that Site into one group.
 */
const SrsFunctionGroupDetailPage = (): React.ReactElement => {
  const { sectionId = '' } = useParams<{ sectionId: string }>()
  const decodedSectionId = decodeRouteParam(sectionId)
  const [searchParams] = useSearchParams()
  const { data = [], isLoading, isError } = useGetPlantProtectiveFunctionsQuery()
  const [search, setSearch] = React.useState(() => searchParams.get('q') ?? '')
  const index = React.useMemo(() => groupProtectiveFunctionsBySrsGroup(data), [data])
  const group = React.useMemo(
    () => findSrsFunctionGroupBySectionId(index, decodedSectionId),
    [decodedSectionId, index]
  )
  const functions = React.useMemo(
    () => (group?.functions ?? []).filter((fn) => matchesProtectiveFunctionSearch(fn, search)),
    [group?.functions, search]
  )
  const categories = React.useMemo(() => groupFunctionsByCategory(functions), [functions])

  return (
    <div className="flex flex-1 flex-col gap-card">
      <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
        <Link to={plantSafetyIndexHref()} className="hover:text-ink hover:underline">
          Safety
        </Link>
        <span className="mx-1.5 text-ink-4">/</span>
        <span className="text-ink">{group?.name ?? 'Function group'}</span>
      </nav>

      {isError ? (
        <DetailFallback
          title="Couldn't load this function group"
          hint="The group may have been deleted or the proxy is unreachable."
        />
      ) : isLoading ? (
        <DetailFallback title="Loading function group…" hint="" muted />
      ) : !group ? (
        <DetailFallback
          title="Function group not found"
          hint="The SRS section may have been removed or no longer has a function group."
        />
      ) : (
        <>
          <header className="flex items-start gap-4 rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
            <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
              <LayersIcon size={22} aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <h1 className="m-0 break-words text-2xl font-semibold text-ink">{group.name}</h1>
              <p className="m-0 mt-1 text-sm text-ink-3">
                {group.site?.name ? `${group.site.name} · ` : ''}
                {countLabel(group.functions.length, 'SIF')} ·{' '}
                {countLabel(group.sections.length, 'SRS section')}
              </p>
            </div>
          </header>

          <div className="flex items-center gap-2">
            <SearchIcon size={14} className="text-ink-3" aria-hidden />
            <Input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search SIFs by name, category, SIL, unit, or solver…"
              className="h-8 max-w-md text-xs"
            />
            <span className="text-xs text-ink-3">
              {functions.length} of {group.functions.length}
            </span>
          </div>

          {functions.length === 0 ? (
            <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
              <Empty
                title="No matching safety functions"
                hint="Try a different search."
                icon={SearchIcon}
              />
            </div>
          ) : (
            categories.map(({ name, functions: categoryFunctions }) => (
              <section key={name}>
                <SectionHeader
                  label={name}
                  subtitle={countLabel(categoryFunctions.length, 'SIF')}
                />
                <div className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
                  <div className="divide-y">
                    {categoryFunctions.map((fn) => (
                      <FunctionRow key={fn.id} fn={fn} />
                    ))}
                  </div>
                </div>
              </section>
            ))
          )}
        </>
      )}
    </div>
  )
}

function FunctionRow({ fn }: { fn: PlantProtectiveFunctionSummary }): React.ReactElement {
  const requiredSil = formatSilLevel(fn.required_sil)
  const achievedSil = formatSilLevel(fn.sil)
  return (
    <Link
      to={plantProtectiveFunctionHref(fn.id)}
      className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 text-card-foreground no-underline hover:bg-accent hover:text-accent-foreground md:grid-cols-[minmax(0,1fr)_auto_auto_auto_auto]"
    >
      <span className="flex min-w-0 items-center gap-2">
        <ShieldIcon size={14} className="shrink-0 text-ink-4" aria-hidden />
        <span className="truncate font-medium text-ink">{fn.name}</span>
      </span>
      {fn.function_type ? (
        <Badge variant="secondary">{protectiveFunctionTypeLabel(fn.function_type)}</Badge>
      ) : (
        <span />
      )}
      <span className="hidden min-w-32 flex-col items-start gap-1 text-xs text-ink-3 md:flex">
        <span className="whitespace-nowrap">
          Required {requiredSil ?? '—'} · Achieved {achievedSil ?? '—'}
        </span>
        <SilRequirementBadge requiredSil={fn.required_sil} achievedSil={fn.sil} showUnavailable />
      </span>
      <span className="hidden truncate text-xs text-ink-3 md:block">
        {fn.line?.name || 'No unit'}
      </span>
      <span className="flex items-center gap-2 text-xs text-ink-3">
        <span className="hidden max-w-40 truncate xl:block">{fn.solver?.name}</span>
        <CaretRightIcon size={14} className="shrink-0 text-ink-4" aria-hidden />
      </span>
    </Link>
  )
}

function groupFunctionsByCategory(
  functions: PlantProtectiveFunctionSummary[]
): Array<{ name: string; functions: PlantProtectiveFunctionSummary[] }> {
  const byCategory = new Map<string, PlantProtectiveFunctionSummary[]>()
  for (const fn of functions) {
    const category = fn.section?.function_category?.trim() || 'Uncategorized'
    const rows = byCategory.get(category) ?? []
    rows.push(fn)
    byCategory.set(category, rows)
  }
  return Array.from(byCategory, ([name, rows]) => ({ name, functions: rows })).sort((a, b) =>
    a.name.localeCompare(b.name)
  )
}

function countLabel(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

function DetailFallback({
  title,
  hint,
  muted
}: {
  title: string
  hint: string
  muted?: boolean
}): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      {muted ? (
        <div className="text-sm text-ink-3">{title}</div>
      ) : (
        <Empty title={title} hint={hint} icon={WarningCircleIcon} />
      )}
    </div>
  )
}

export default SrsFunctionGroupDetailPage
